import { FunnelSimple } from "@phosphor-icons/react";
import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import CampaignSelector from "./CampaignSelector";
import LabelSelector from "./LabelSelector";

interface SelectedFilters {
  accountIds: string[];
  campaignIds: string[];
  labelIds: string[];
}

interface FilterConversationsProps {
  activeFilters: SelectedFilters;
  onSetActiveFilters: (filters: {
    accountIds: string[];
    campaignIds: string[];
    labelIds: string[];
  }) => void;
}

export default function FilterConversations({
  activeFilters,
  onSetActiveFilters,
}: FilterConversationsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    accountIds: activeFilters.accountIds,
    campaignIds: activeFilters.campaignIds,
    labelIds: activeFilters.labelIds,
  });

  function applyFilters(): void {
    const { accountIds, campaignIds, labelIds } = selectedFilters;

    onSetActiveFilters({
      accountIds,
      campaignIds,
      labelIds,
    });

    setIsDialogOpen(false);
  }

  function clearFilters(): void {
    setSelectedFilters({
      accountIds: [],
      campaignIds: [],
      labelIds: [],
    });
  }

  function handleDialogChange(isOpen: boolean): void {
    setIsDialogOpen(isOpen);

    if (isOpen) {
      setSelectedFilters({
        accountIds: activeFilters.accountIds,
        campaignIds: activeFilters.campaignIds,
        labelIds: activeFilters.labelIds,
      });
    }
  }

  const hasActiveFilters =
    activeFilters.accountIds.length > 0 ||
    activeFilters.campaignIds.length > 0 ||
    activeFilters.labelIds.length > 0;

  const isClearButtonDisabled =
    !selectedFilters.accountIds.length &&
    !selectedFilters.campaignIds.length &&
    !selectedFilters.labelIds?.length;

  const updateSelectedFilter =
    (key: keyof SelectedFilters) => (value: string[]) => {
      setSelectedFilters((prev) => ({ ...prev, [key]: value }));
    };

  const totalFilterCount =
    selectedFilters.accountIds.length +
    selectedFilters.campaignIds.length +
    (selectedFilters.labelIds?.length ?? 0);

  return (
    <Component open={isDialogOpen} onOpenChange={handleDialogChange}>
      <Trigger asChild>
        <Button
          intent="iconOnly"
          variant="outline-black"
          className="group relative"
        >
          <FunnelSimple />

          <RenderIf condition={hasActiveFilters}>
            <span className="absolute right-0 top-0 flex size-4 items-center justify-center rounded-full bg-purple-500 px-1 text-button-xxs text-white">
              {totalFilterCount}
            </span>
          </RenderIf>
        </Button>
      </Trigger>

      <Content>
        <Header
          title="Inbox filters"
          subtitle="Filter conversations by account, campaign, and label"
        />

        <Body className="gap-4">
          <FormField
            label="Filter by account"
            htmlFor="account-select"
            size="lg"
          >
            <WorkspaceAccountSelector
              selection={selectedFilters.accountIds}
              setSelection={updateSelectedFilter("accountIds")}
            />
          </FormField>

          <FormField
            label="Filter by campaign"
            htmlFor="campaign-select"
            size="lg"
          >
            <CampaignSelector
              selectedCampaignIds={selectedFilters.campaignIds}
              setSelectedCampaignIds={updateSelectedFilter("campaignIds")}
            />
          </FormField>

          <FormField label="Filter by labels" htmlFor="label-select" size="lg">
            <LabelSelector
              selectedLabelIds={selectedFilters.labelIds}
              setSelectedLabelIds={updateSelectedFilter("labelIds")}
            />
          </FormField>
        </Body>

        <Footer className="justify-end">
          <Button
            variant="outline-black"
            onClick={clearFilters}
            disabled={isClearButtonDisabled}
          >
            Clear all filters
          </Button>

          <Button onClick={applyFilters}>Apply filters</Button>
        </Footer>
      </Content>
    </Component>
  );
}
